/** t331u B2: phone Person › Positions (desktop PositionsTab): held positions with a starred primary, Add, Undo / Save, history. */
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Plus, Star, X } from 'lucide-react';
import { describePositionsChange, normalisePositions, positionsPatch, POSITIONS_HISTORY_ACTIONS, samePositions, type Employee, type PositionValue } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { fmt } from '@/lib/format';
import { Spinner } from '@/components/ui';
import { useWfLookups } from '@/features/workforce/common';
import { BottomSheet } from '../BottomSheet';
import { ghostBtn, head, saveBtn } from './PersonSettings';

export function PositionsPage({ emp, name }: { emp: Employee; name: string }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const { positions } = useWfLookups();
  const initial = useMemo(() => normalisePositions({ ids: emp.position_ids, primary: emp.primary_position_id ?? null }), [emp]);
  const [v, setV] = useState<PositionValue>(initial);
  useEffect(() => setV(initial), [initial]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const hist = useQuery({ queryKey: ['wf', ws, 'employee-history', emp.user_id, 'positions'], queryFn: () => api.wf.employeeHistory(ws!, emp.user_id, POSITIONS_HISTORY_ACTIONS), enabled: Boolean(ws) });
  const byId = new Map(positions.map(p => [p.id, p]));
  const set = (n: PositionValue) => setV(normalisePositions(n));
  const dirty = !samePositions(v, initial);
  const options = positions.filter(p => !p.archived_at && !v.ids.includes(p.id));
  const save = async () => {
    setBusy(true);
    try { await api.wf.updateEmployee(ws!, emp.user_id, positionsPatch(v)); toast('Positions saved', 'success'); void queryClient.invalidateQueries({ queryKey: ['wf', ws] }); }
    catch (e) { toast(errorMessage(e), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-positions">
        {name && <p className="truncate px-4 pt-3 text-base font-semibold" data-testid="m-emp-name">{name}</p>}
        <h3 className={head}>Positions</h3>
        <div className="mx-3 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900" data-testid="m-positions-list">
          {v.ids.map(id => {
            const p = byId.get(id); const primary = v.primary === id; const label = p?.name ?? 'Unknown position';
            return (
              <div key={id} className="flex min-h-[48px] items-center gap-3 border-b border-slate-100 pl-3 dark:border-slate-800" data-testid={`m-positions-row-${id}`} data-primary={primary || undefined}>
                <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: p?.color ?? '#94a3b8' }} aria-hidden />
                <span className="min-w-0 flex-1 truncate text-base">{label}{primary && <span className="sr-only"> (primary)</span>}</span>
                <button type="button" aria-pressed={primary} aria-label={primary ? `${label} is the primary position` : `Make ${label} the primary position`} onClick={() => set({ ...v, primary: id })}
                  className="flex h-11 w-11 items-center justify-center"><Star className={clsx('h-5 w-5', primary ? 'fill-current text-ink dark:text-white' : 'text-slate-400')} aria-hidden /></button>
                <button type="button" aria-label={`Remove ${label}`} onClick={() => set({ ...v, ids: v.ids.filter(x => x !== id) })} className="-ml-1 flex h-11 w-11 items-center justify-center text-slate-500"><X className="h-5 w-5" aria-hidden /></button>
              </div>
            );
          })}
          {!v.ids.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No positions yet.</p>}
          <button type="button" onClick={() => setAdding(true)} data-testid="m-positions-add" className="flex min-h-[48px] w-full items-center gap-3 px-3 text-left text-base font-medium active:bg-slate-100 dark:active:bg-slate-800">
            <Plus className="h-5 w-5" aria-hidden />Add a position</button>
        </div>
        <h3 className={head}>History</h3>
        <div className="mx-3 rounded-2xl border border-slate-200 bg-white px-3 py-1 dark:border-slate-800 dark:bg-slate-900" data-testid="m-positions-history">
          {hist.isLoading ? <div className="py-3"><Spinner /></div> : !hist.data?.entries.length ? <p className="py-2 text-sm text-slate-500">No changes recorded yet.</p>
            : hist.data.entries.map(e => (
              <div key={e.id} className="border-b border-slate-100 py-2 text-sm last:border-0 dark:border-slate-800">
                <p>{describePositionsChange(e.metadata)}</p>
                <p className="text-xs text-slate-500"><time dateTime={e.created_at}>{fmt.dateTime(e.created_at)}</time> · {e.actor_name ?? 'System'}</p>
              </div>
            ))}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 border-t border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950" data-testid="m-positions-savebar">
        <button type="button" className={ghostBtn} disabled={!dirty || busy} onClick={() => setV(initial)}>Undo</button>
        <button type="button" className={saveBtn} disabled={!dirty || busy} data-testid="m-positions-save" onClick={() => void save()}>Save</button>
      </div>
      <BottomSheet open={adding} onClose={() => setAdding(false)} title="Add a position" testId="m-positions-pick">
        <ul>
          {options.map(p => (
            <li key={p.id}><button type="button" data-testid={`m-positions-pick-${p.id}`} onClick={() => { set({ ...v, ids: [...v.ids, p.id] }); setAdding(false); }}
              className="flex min-h-[48px] w-full items-center gap-3 px-4 text-left text-base active:bg-slate-100 dark:active:bg-slate-800">
              <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: p.color }} aria-hidden />{p.name}</button></li>
          ))}
          {!options.length && <li className="px-4 py-3 text-sm text-slate-500">All positions added.</li>}
        </ul>
      </BottomSheet>
    </>
  );
}
